// AutoPublishModal — One-click publish to WhatsApp + Instagram from a completed Veo job
import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AutoPublishModal({ token, job, language = 'hi', onClose, onPublished }) {
  const [caption, setCaption] = useState('');
  const [captionLoading, setCaptionLoading] = useState(true);
  const [captionLanguage, setCaptionLanguage] = useState('hi');
  const [channels, setChannels] = useState({ whatsapp: false, instagram: true });
  const [recipientsRaw, setRecipientsRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [result, setResult] = useState(null);
  // Scheduling state
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' | 'best' | 'custom'
  const [customScheduleAt, setCustomScheduleAt] = useState('');
  const [bestHourLabel, setBestHourLabel] = useState(null);

  const loadCaption = async (lang = captionLanguage) => {
    setCaptionLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/veo/publish/preview/${job.id}?language=${lang}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.success) setCaption(d.caption || '');
    } catch (e) { console.warn('caption preview:', e); }
    setCaptionLoading(false);
  };

  useEffect(() => { loadCaption(); /* eslint-disable-next-line */ }, []);

  // Fetch best_publish_hour for the "Schedule at best hour" option
  useEffect(() => {
    fetch(`${API_URL}/api/veo/analytics/recommendations?range=30d`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.success && d.best_publish_hour) setBestHourLabel(d.best_publish_hour.label); })
      .catch(() => {});
  }, [token]);

  const submit = async () => {
    const chans = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    if (chans.length === 0) {
      setMsg({ type: 'error', text: language === 'hi' ? 'कम से कम एक channel select करें' : 'Select at least one channel' });
      return;
    }
    let recipients = [];
    if (channels.whatsapp) {
      recipients = recipientsRaw
        .split(/[\s,;\n]+/)
        .map((s) => s.trim().replace(/[^\d+]/g, ''))
        .filter((s) => s.length >= 10);
      if (recipients.length === 0) {
        setMsg({ type: 'error', text: language === 'hi' ? 'WhatsApp के लिए कम से कम 1 phone number डालें' : 'Add at least 1 WhatsApp recipient phone' });
        return;
      }
      if (recipients.length > 50) {
        setMsg({ type: 'error', text: language === 'hi' ? 'Max 50 recipients allowed' : 'Max 50 recipients allowed' });
        return;
      }
    }
    setSubmitting(true); setMsg(null);
    try {
      const body = { channels: chans, caption, caption_language: captionLanguage };
      if (channels.whatsapp) body.whatsapp_recipients = recipients;

      // Schedule path
      if (scheduleMode !== 'now') {
        if (scheduleMode === 'best') body.auto_schedule = true;
        else if (scheduleMode === 'custom') {
          if (!customScheduleAt) { setMsg({ type: 'error', text: language === 'hi' ? 'Custom date+time चुनें' : 'Pick a custom date+time' }); setSubmitting(false); return; }
          body.scheduled_at = new Date(customScheduleAt).toISOString();
        }
        const sr = await fetch(`${API_URL}/api/veo/publish/schedule/${job.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        const sd = await sr.json();
        if (sr.ok && sd.success) {
          setMsg({ type: 'success', text: (language === 'hi' ? '⏰ Scheduled at ' : '⏰ Scheduled at ') + new Date(sd.scheduled_at).toLocaleString() + (sd.scheduled_from ? ` (${sd.scheduled_from})` : '') });
        } else {
          setMsg({ type: 'error', text: sd.detail || 'Schedule failed' });
        }
        setSubmitting(false);
        return;
      }

      const r = await fetch(`${API_URL}/api/veo/publish/${job.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        setMsg({ type: 'success', text: language === 'hi' ? `✅ Queued! Publish ID: ${d.publish_id}` : `✅ Queued! Publish ID: ${d.publish_id}` });
        // Poll for completion
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          try {
            const sr = await fetch(`${API_URL}/api/veo/publish/${d.publish_id}`, { headers: { Authorization: `Bearer ${token}` } });
            const sd = await sr.json();
            if (sd.success) {
              const p = sd.publish;
              if (p.status === 'completed' || p.status === 'failed' || p.status === 'partial_failure') {
                setResult(p);
                if (onPublished) onPublished(p);
                return;
              }
            }
          } catch {}
          if (attempts < 60) setTimeout(poll, 3000);
        };
        setTimeout(poll, 2000);
      } else {
        setMsg({ type: 'error', text: d.detail || 'Submit failed' });
      }
    } catch (e) { setMsg({ type: 'error', text: 'Error: ' + e.message }); }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="auto-publish-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-600 text-white p-5 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-black" style={{ fontFamily: 'Outfit' }}>📢 Auto-Publish</h3>
              <p className="text-xs text-white/90 mt-1">{job.title || job.id}</p>
            </div>
            <button onClick={onClose} className="text-white text-2xl leading-none" data-testid="publish-close-btn">×</button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Channel toggles */}
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-2">{language === 'hi' ? 'Channels' : 'Channels'}</p>
            <div className="grid grid-cols-2 gap-3">
              <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${channels.instagram ? 'border-pink-500 bg-pink-50' : 'border-gray-200'}`} data-testid="channel-instagram">
                <input type="checkbox" checked={channels.instagram} onChange={(e) => setChannels({ ...channels, instagram: e.target.checked })} className="w-4 h-4" />
                <div className="flex-1">
                  <div className="font-bold text-sm">📷 Instagram Reel</div>
                  <div className="text-[11px] text-gray-500">School के connected IG account पर post</div>
                </div>
              </label>
              <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${channels.whatsapp ? 'border-green-500 bg-green-50' : 'border-gray-200'}`} data-testid="channel-whatsapp">
                <input type="checkbox" checked={channels.whatsapp} onChange={(e) => setChannels({ ...channels, whatsapp: e.target.checked })} className="w-4 h-4" />
                <div className="flex-1">
                  <div className="font-bold text-sm">💬 WhatsApp</div>
                  <div className="text-[11px] text-gray-500">Custom phone list पर video भेजें</div>
                </div>
              </label>
            </div>
          </div>

          {/* Caption */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-700">{language === 'hi' ? 'Caption' : 'Caption'}</p>
              <div className="inline-flex bg-slate-100 rounded p-0.5 text-xs">
                {['hi', 'en'].map((l) => (
                  <button key={l} onClick={() => { setCaptionLanguage(l); loadCaption(l); }} className={`px-2 py-0.5 rounded ${captionLanguage === l ? 'bg-white shadow font-semibold' : ''}`} data-testid={`caption-lang-${l}`}>
                    {l === 'hi' ? 'हिं' : 'EN'}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={6}
              maxLength={2200}
              placeholder={captionLoading ? 'Loading auto-caption…' : 'Caption…'}
              className="w-full px-3 py-2 border rounded-lg text-sm font-sans"
              data-testid="publish-caption"
            />
            <p className="text-[10px] text-gray-400 mt-1">{caption.length}/2200 chars</p>
          </div>

          {/* WhatsApp recipients */}
          {channels.whatsapp && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">
                {language === 'hi' ? 'WhatsApp Recipients (E.164 format, comma/newline separated, max 50)' : 'WhatsApp Recipients (E.164, comma/newline, max 50)'}
              </p>
              <textarea
                value={recipientsRaw}
                onChange={(e) => setRecipientsRaw(e.target.value)}
                rows={3}
                placeholder="919876543210, 919812345678"
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                data-testid="publish-recipients"
              />
              {(() => {
                const count = recipientsRaw.split(/[\s,;\n]+/).map((s) => s.trim().replace(/[^\d+]/g, '')).filter((s) => s.length >= 10).length;
                const over = count > 50;
                return (
                  <p className={`text-[11px] mt-1 ${over ? 'text-red-600 font-bold' : 'text-gray-400'}`} data-testid="recipients-count">
                    {count} valid number{count === 1 ? '' : 's'}{over ? ' — Max 50 allowed' : ' / 50'}
                  </p>
                );
              })()}
            </div>
          )}

          {/* Scheduling */}
          <div className="border-t pt-3" data-testid="schedule-section">
            <p className="text-xs font-semibold text-gray-700 mb-2">
              📅 {language === 'hi' ? 'Schedule (optional)' : 'Schedule (optional)'}
            </p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <button
                onClick={() => setScheduleMode('now')}
                className={`px-2 py-1.5 rounded border-2 ${scheduleMode === 'now' ? 'border-purple-500 bg-purple-50 font-semibold' : 'border-gray-200'}`}
                data-testid="schedule-mode-now"
              >🚀 {language === 'hi' ? 'अभी' : 'Now'}</button>
              <button
                onClick={() => setScheduleMode('best')}
                disabled={!bestHourLabel}
                className={`px-2 py-1.5 rounded border-2 disabled:opacity-50 ${scheduleMode === 'best' ? 'border-amber-500 bg-amber-50 font-semibold' : 'border-gray-200'}`}
                data-testid="schedule-mode-best"
              >⏰ {language === 'hi' ? 'Best Hour' : 'Best Hour'}{bestHourLabel ? <div className="text-[10px] text-gray-500">{bestHourLabel}</div> : <div className="text-[10px] text-gray-400">no data</div>}</button>
              <button
                onClick={() => setScheduleMode('custom')}
                className={`px-2 py-1.5 rounded border-2 ${scheduleMode === 'custom' ? 'border-blue-500 bg-blue-50 font-semibold' : 'border-gray-200'}`}
                data-testid="schedule-mode-custom"
              >🗓 {language === 'hi' ? 'Custom' : 'Custom'}</button>
            </div>
            {scheduleMode === 'custom' && (
              <input
                type="datetime-local"
                value={customScheduleAt}
                onChange={(e) => setCustomScheduleAt(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm mt-2"
                data-testid="schedule-custom-input"
              />
            )}
          </div>

          {/* Messages + result */}
          {msg && (
            <div className={`text-sm px-3 py-2 rounded ${msg.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-testid="publish-msg">
              {msg.text}
            </div>
          )}

          {result && (
            <div className="bg-slate-50 rounded-xl p-3 border space-y-2" data-testid="publish-result">
              <p className="font-bold text-sm">📊 Publish Result — {result.status}</p>
              {result.results?.instagram && (
                <div className="text-xs">
                  <span className="font-semibold">Instagram:</span>{' '}
                  {result.results.instagram.success
                    ? <a href={result.results.instagram.permalink} target="_blank" rel="noreferrer" className="text-blue-600 underline">View Reel</a>
                    : <span className="text-red-600">❌ {result.results.instagram.error}</span>}
                </div>
              )}
              {result.results?.whatsapp && (
                <div className="text-xs">
                  <span className="font-semibold">WhatsApp:</span>{' '}
                  ✅ {result.results.whatsapp.succeeded} sent
                  {result.results.whatsapp.failed > 0 && <span className="text-red-600 ml-2">❌ {result.results.whatsapp.failed} failed</span>}
                </div>
              )}
            </div>
          )}

          {/* Action */}
          <div className="flex gap-2 pt-2 border-t">
            <button onClick={onClose} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-semibold" data-testid="publish-cancel-btn">
              {language === 'hi' ? 'बंद करें' : 'Close'}
            </button>
            <button
              onClick={submit}
              disabled={submitting || !caption.trim()}
              className="flex-1 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-lg disabled:opacity-50"
              data-testid="publish-submit-btn"
            >
              {submitting
                ? (language === 'hi' ? 'Working…' : 'Working…')
                : scheduleMode === 'now'
                  ? (language === 'hi' ? '🚀 Publish करें' : '🚀 Publish Now')
                  : scheduleMode === 'best'
                    ? (language === 'hi' ? '⏰ Best Hour पर Schedule' : '⏰ Schedule for Best Hour')
                    : (language === 'hi' ? '🗓 Custom Time पर Schedule' : '🗓 Schedule at Custom Time')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
